import React, { useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import SearchIcon from '@mui/icons-material/Search'
import TemplateCard from '../components/TemplateCard'
import { MOCK_TEMPLATES } from '../mockData'
import { useGetTemplatesQuery } from '../../../redux/interventionApiSlice'

function TemplateCardSkeleton() {
  return (
    <Paper sx={{ p: 2, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: '#E0E4E8' }} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pl: 1 }}>
        <Skeleton variant='rounded' width={36} height={36} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width='60%' height={20} />
          <Skeleton width='80%' height={14} sx={{ mt: 0.5 }} />
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
            <Skeleton variant='rounded' width={72} height={22} />
            <Skeleton variant='rounded' width={56} height={22} />
          </Box>
          <Skeleton width='40%' height={14} sx={{ mt: 1 }} />
        </Box>
      </Box>
    </Paper>
  )
}

const USE_MOCK = false

export default function TemplateRegistry() {
  const [channel, setChannel] = useState('ALL')
  const [type, setType] = useState('ALL')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('usage')

  const { data: apiTemplates, isFetching } = useGetTemplatesQuery(undefined, { skip: USE_MOCK })
  const isLoading = USE_MOCK ? false : isFetching
  const templates = USE_MOCK ? MOCK_TEMPLATES : (apiTemplates || [])

  const filtered = useMemo(() => {
    let arr = [...templates]

    if (channel !== 'ALL') arr = arr.filter(t => t.channel === channel)
    if (type !== 'ALL') arr = arr.filter(t => t.applicableTo === type || !t.applicableTo)
    if (search) {
      const q = search.toLowerCase()
      arr = arr.filter(t =>
        t.templateName?.toLowerCase().includes(q) ||
        t.displayName?.toLowerCase().includes(q)
      )
    }

    switch (sort) {
      case 'usage': arr.sort((a, b) => (b.usageCount30d || 0) - (a.usageCount30d || 0)); break
      case 'name': arr.sort((a, b) => (a.displayName || a.templateName).localeCompare(b.displayName || b.templateName)); break
      case 'recent': arr.sort((a, b) => (b.lastUsedDate || '').localeCompare(a.lastUsedDate || '')); break
      default: break
    }

    return arr
  }, [templates, channel, type, search, sort])

  return (
    <Box>
      <Typography variant='h5' sx={{ mb: 2 }}>Template Registry</Typography>

      <Box className='filter-bar'>
        <ToggleButtonGroup value={channel} exclusive onChange={(_, v) => v && setChannel(v)} size='small'>
          <ToggleButton value='ALL'>All</ToggleButton>
          <ToggleButton value='WHATSAPP'>WhatsApp</ToggleButton>
          <ToggleButton value='SMS'>SMS</ToggleButton>
          <ToggleButton value='EMAIL'>Email</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup value={type} exclusive onChange={(_, v) => v && setType(v)} size='small'>
          <ToggleButton value='ALL'>All Types</ToggleButton>
          <ToggleButton value='NDR'>NDR</ToggleButton>
          <ToggleButton value='NPR'>NPR</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          placeholder='Search templates...'
          size='small'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
          }}
          sx={{ minWidth: 200 }}
        />

        <Select value={sort} onChange={(e) => setSort(e.target.value)} size='small' sx={{ minWidth: 140 }}>
          <MenuItem value='usage'>Sort: Usage</MenuItem>
          <MenuItem value='name'>Sort: Name</MenuItem>
          <MenuItem value='recent'>Sort: Recent</MenuItem>
        </Select>
      </Box>

      <Typography variant='caption' sx={{ color: 'text.disabled', mb: 1.5, display: 'block' }}>
        {filtered.length} template{filtered.length !== 1 ? 's' : ''} found
      </Typography>

      <Box className='template-grid'>
        {isLoading
          ? Array.from({ length: 8 }).map((_, i) => <TemplateCardSkeleton key={i} />)
          : filtered.map(t => <TemplateCard key={t.templateName + t.channel} template={t} />)
        }
      </Box>

      {!isLoading && !filtered.length && (
        <Box className='empty-state'>
          <Typography variant='body2'>No templates match your filters</Typography>
        </Box>
      )}
    </Box>
  )
}
